'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/admin-hub/iam-console.mdx
 * (published at user-docs.totalaccess.co.za/admin-hub/iam-console)
 */

/**
 * IamConsoleModal — full-screen IAM console overlay.
 *
 * Mounts the real IamConsole (Directory / Permissions / Devices / Security
 * hubs) inside a viewport-covering overlay so admins can manage identity and
 * access without navigating away — the same pattern as SupportModal,
 * ProfileModal, MyDevicesModal and DiagnosticModal.
 *
 * The console is loaded via next/dynamic so the Header bundle doesn't pay
 * for it until first opened.
 */

import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { Shield, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigation } from '@/hooks/useNavigation';
import { useTheme } from '@/contexts/ThemeContext';
import { adjustColorBrightness } from '@/lib/themes';
import ModalFooter from '@/components/shared/ModalFooter';
import type { IamModalSection } from '@/components/iam/IamConsole';

export type { IamModalSection };

const IamConsole = dynamic(
  () => import('@/components/iam/IamConsole'),
  { ssr: false },
);

interface IamConsoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Which hub to open on — directory / permissions / devices / security. */
  initialSection?: IamModalSection;
  /** Pill-level navigation request from 'iam-console-open' detail.tab — applied
   *  every time the nonce changes (guided tours deep-link into the console). */
  navRequest?: { tab: string; nonce: number } | null;
}

export default function IamConsoleModal({
  isOpen,
  onClose,
  initialSection = 'directory',
  navRequest,
}: IamConsoleModalProps) {
  const { user: authUser, isRoot } = useAuth();
  const { user: navigationUser, userRole } = useNavigation();
  const { clientBranding } = useTheme();

  // Client-brand chrome: the icon/close accents follow primary_color, matching
  // the active-pill colour inside IamConsole.
  const brandPrimary = clientBranding?.primary_color || '#3b82f6';
  const brandGradient = `linear-gradient(135deg, ${brandPrimary}, ${adjustColorBrightness(brandPrimary, -10)})`;

  // Same access gate as the admin-hub page: owner/root/system_admin only.
  const hasIamAccess = useMemo(() => {
    const roleCandidates = [userRole, navigationUser?.role, authUser?.role]
      .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
      .map((role) => role.toLowerCase());

    return isRoot
      || roleCandidates.includes('root')
      || roleCandidates.includes('owner')
      || roleCandidates.includes('system_admin');
  }, [authUser?.role, isRoot, navigationUser?.role, userRole]);

  const permissions = useMemo(() => ({
    can_view: hasIamAccess,
    can_create: hasIamAccess,
    can_edit: hasIamAccess,
    can_delete: hasIamAccess,
    can_admin: hasIamAccess,
    can_manage_teams: hasIamAccess,
  }), [hasIamAccess]);

  if (!isOpen || !hasIamAccess || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto"
      data-tour-id="iam-console-modal"
      role="dialog"
      aria-modal="true"
      aria-label="IAM Console"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex min-h-full flex-col px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        {/* Header — matches SupportModal / ProfileModal */}
        <div className="mb-3 sm:mb-6 px-2 relative">
          <div className="text-center">
            <div
              className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16"
              style={{ backgroundImage: brandGradient }}
            >
              <Shield className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">Identity & Access Management</h1>
          </div>
          <div className="absolute right-2 top-0 flex items-center gap-2">
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              aria-label="Close IAM console"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-2 flex-1">
          <IamConsole permissions={permissions} initialSection={initialSection} navRequest={navRequest} />
        </div>

        <ModalFooter>
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto justify-center px-6 py-2.5 rounded-lg font-medium text-white transition-all flex items-center gap-2"
            style={{ backgroundImage: brandGradient }}
          >
            Close
          </button>
        </ModalFooter>
      </div>
    </div>,
    document.body,
  );
}
